import {
  Table,
  Pagination,
  Button,
  Popconfirm,
  Input,
  Tag,
  Tooltip,
} from "antd";
import {
  MdOutlineDelete,
  MdOutlineEdit,
  MdOutlineRemoveRedEye,
  MdSearch,
} from "react-icons/md";
import { AiOutlineMobile, AiOutlineShareAlt } from "react-icons/ai";
import { FaInternetExplorer } from "react-icons/fa";
import moment from "moment";
import { useState } from "react";

function DataTable(props) {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const onSelectChange = (newSelectedRowKeys, rowDetails) => {
    props.onSelect(rowDetails);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          size="middle"
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <div style={{ margin: 5 }} />
        <Button
          type="primary"
          size="small"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
        >
          Search
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size="small">
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <div style={{ backgroundColor: "transparent" }}>
        <MdSearch />
      </div>
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const calculateValues = (record) => {
    const { boxes, ledgerTransColln } = record || {};

    // Calculate total weight and invoice charge
    let totalWeight = 0;
    let totalAmount = 0;
    boxes?.forEach((val) => {
      totalWeight += Number(val.weight || 0);
      totalAmount += Number(val.total_charge || 0);
    });

    // Calculate paid amount and discount
    let totalPaidAmount = 0;
    let totalDiscount = 0;
    boxes?.forEach((box) => {
      const boxNumber = box.box_number;
      const boxLedgerEntries = ledgerTransColln?.filter(
        (entry) => entry.note === boxNumber && entry.cr_or_dr === "Cr"
      );
      if (boxLedgerEntries) {
        const boxPaid = boxLedgerEntries.reduce(
          (sum, entry) => sum + Number(entry.amount || 0),
          0
        );
        const boxDiscount = boxLedgerEntries.reduce(
          (sum, entry) => sum + Number(entry.box_discount || 0),
          0
        );
        totalPaidAmount += boxPaid;
        totalDiscount += boxDiscount;
      }
    });

    // Calculate payment status and balance
    const totalPaidWithDiscount = totalPaidAmount + totalDiscount;
    const tolerance = 0.01;
    let paymentStatus = "";
    let paymentColor = "";
    const balance = totalAmount - totalPaidWithDiscount;

    if (Math.abs(totalPaidWithDiscount - totalAmount) < tolerance) {
      paymentStatus = "Paid";
      paymentColor = "green";
    } else if (
      totalPaidWithDiscount > 0 &&
      totalPaidWithDiscount < totalAmount
    ) {
      paymentStatus = "Partially Paid";
      paymentColor = "orange";
    } else {
      paymentStatus = "Unpaid";
      paymentColor = "red";
    }

    return {
      totalWeight: totalWeight.toFixed(1),
      totalAmount: totalAmount.toFixed(2),
      totalPaid: totalPaidAmount.toFixed(2),
      totalDiscount: totalDiscount.toFixed(2),
      balance: balance.toFixed(2),
      paymentStatus,
      paymentColor,
    };
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: "20px",
      render: (text, record, index) =>
        (props.meta.page - 1) * props.meta.take + index + 1,
    },
    {
      title: "Collection Id",
      dataIndex: "id",
      key: "id",
      render: (id) => <div className="collection-table-item">{id}</div>,
    },
    {
      title: "",
      render: (text, record) => (
        <div>
          {record.created_from && record.created_from === "web" ? (
            <FaInternetExplorer size={15} />
          ) : (
            <AiOutlineMobile size={15} />
          )}
        </div>
      ),
    },
    {
      title: "DATE",
      dataIndex: "createdat",
      key: "createdat",
      render: (createdat) => (
        <div className="collection-table-item">
          {moment(createdat).format("l")}
        </div>
      ),
    },
    {
      title: "BOOKING",
      dataIndex: "",
      key: "",
      render: (text, record) => (
        <Tooltip
          title={
            <div style={{ fontFamily: "Campton-Light" }}>
              {record.boxes &&
                record.boxes.map((item, index) => (
                  <div key={index}>{`BOX ${index + 1} : ${
                    item.box_number
                  }`}</div>
                ))}
            </div>
          }
        >
          <div className="collection-table-column">
            {record.boxes &&
              record.boxes.map((item, index) => (
                <div
                  key={index}
                  className="collection-table-item2"
                  style={{ fontSize: "11px" }}
                >
                  {item.box_number}
                </div>
              ))}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "PCS",
      dataIndex: "number_boxes",
      key: "number_boxes",
      render: (text) => <div className="collection-table-item">{text}</div>,
    },
    {
      title: "TOTAL KILO",
      dataIndex: "total_weight",
      key: "total_weight",
      render: (text, record) => {
        const { totalWeight } = calculateValues(record);
        return <div className="collection-table-item">{totalWeight}</div>;
      },
      width: 50,
    },
    {
      title: "INVOICE CHARGE",
      dataIndex: "total_pay",
      key: "total_pay",
      render: (text, record) => {
        const { totalAmount } = calculateValues(record);
        return <div className="collection-table-item">{totalAmount}</div>;
      },
    },
    {
      title: "DISCOUNT",
      dataIndex: "id",
      key: "id",
      render: (collnId, record) => {
        const { totalDiscount } = calculateValues(record);
        return <div className="collection-table-item">{totalDiscount}</div>;
      },
    },
    {
      title: "PAID",
      dataIndex: "id",
      key: "id",
      render: (collnId, record) => {
        const { totalPaid } = calculateValues(record);
        return <div className="collection-table-item">{totalPaid}</div>;
      },
    },
    {
      title: "BALANCE",
      dataIndex: "id",
      key: "id",
      render: (collnId, record) => {
        const { balance } = calculateValues(record);
        return (
          <div className="collection-table-item" style={{ color: "red" }}>
            {balance}
          </div>
        );
      },
    },
    {
      title: "STATUS",
      dataIndex: "id",
      key: "id",
      render: (collnId, record) => {
        const { paymentStatus, paymentColor } = calculateValues(record);
        return <Tag color={paymentColor}>{paymentStatus}</Tag>;
      },
    },
    {
      title: "SENDER",
      dataIndex: "sender_name",
      key: "sender_name",
      render: (text) => <div className="collection-table-item">{text}</div>,
      ...getColumnSearchProps("sender_name"),
      width: 150,
    },
    {
      title: "RECEIVER",
      dataIndex: "reciver_name",
      key: "reciver_name",
      render: (text) => <div className="collection-table-item">{text}</div>,
      ...getColumnSearchProps("reciver_name"),
      width: 150,
    },
    {
      title: "LOG ID",
      dataIndex: "collected_by",
      key: "collected_by",
      render: (text, record) => (
        <div className="collection-table-item">
          {record.boxes &&
            record.boxes[0] &&
            record.boxes[0]?.created_users?.name}
        </div>
      ),
    },
    {
      title: "LOG BRANCH",
      dataIndex: "",
      key: "",
      render: (text, record) => (
        <div className="collection-table-item">
          {record.boxes &&
            record.boxes[0] &&
            record.boxes[0]?.created_users?.branch?.name}
        </div>
      ),
    },
    {
      title: "Actions",
      fixed: "right",
      render: (item) => (
        <div className="tableAction">
          <Button size="small" onClick={() => props.details(item)} type="link">
            <MdOutlineRemoveRedEye size={20} color={"#78909c"} />
          </Button>
          <Button size="small" type="link" onClick={() => props.share(item)}>
            <AiOutlineShareAlt size={20} />
          </Button>
          <Button size="small" onClick={() => props.update(item)} type="link">
            <MdOutlineEdit size={20} color={"#ffa726"} />
          </Button>
          <Popconfirm
            placement="bottomRight"
            title={"Are you sure to delete?"}
            okText="Yes"
            cancelText="No"
            onConfirm={() => props.delete(item)}
          >
            <Button size="small" type="link">
              <MdOutlineDelete size={20} />
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div style={{ minHeight: "60vh" }}>
      <Table
        rowClassName={(record, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
        columns={columns}
        dataSource={props.data}
        pagination={false}
        size="small"
        rowKey={(record) => record.id}
        rowSelection={{ type: "radio", ...rowSelection }}
        scroll={{ x: true }}
      />
      {!props.isFiltered && (
        <>
          <br />
          <div align="right">
            <Pagination
              responsive
              defaultCurrent={props.meta && props.meta.page}
              total={props.meta && props.meta.itemCount}
              pageSize={props.meta && props.meta.take ? props.meta.take : 0}
              onChange={props.PageOnchange}
              pageSizeOptions={[10, 20, 50]}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default DataTable;
